import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, Rating,
    TableHead, TableRow, CircularProgress, Alert, Chip, Checkbox, IconButton,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const FeedbackManagement = () => {
    const [feedbackList, setFeedbackList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState([]);
    const [selectionMode, setSelectionMode] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, ids: [], message: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchFeedback = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('jwtToken');
            const { data } = await axios.get(`${API_BASE_URL}/api/feedback/admin`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFeedbackList(data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch feedback.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFeedback();
    }, []);

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = feedbackList.map((n) => n.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleSelect = (id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const handleExitSelectionMode = () => {
        setSelectionMode(false);
        setSelected([]);
    };

    const handleEnterSelectionMode = (id) => {
        setSelectionMode(true);
        setSelected([id]);
    };

    const handleDeleteRequest = (ids) => {
        const message = ids.length > 1
            ? `Are you sure you want to delete these ${ids.length} feedback items? This action is permanent.`
            : 'Are you sure you want to delete this feedback item? This action is permanent.';

        setConfirmDialog({
            open: true,
            ids: ids,
            message: message,
        });
    };

    const handleConfirmDelete = async () => {
        const idsToDelete = confirmDialog.ids;
        if (idsToDelete.length === 0) return;

        try {
            const token = localStorage.getItem('jwtToken');
            const headers = { Authorization: `Bearer ${token}` };

            if (idsToDelete.length === 1) {
                await axios.delete(`${API_BASE_URL}/api/feedback/admin/${idsToDelete[0]}`, { headers });
            } else {
                await axios.post(`${API_BASE_URL}/api/feedback/admin/delete-multiple`, { ids: idsToDelete }, { headers });
            }

            setFeedbackList(prev => prev.filter(item => !idsToDelete.includes(item.id)));
            setSnackbar({ open: true, message: 'Feedback deleted successfully', severity: 'success' });
        } catch (err) {
            console.error('Failed to delete feedback:', err);
            setSnackbar({ open: true, message: 'Failed to delete feedback', severity: 'error' });
        } finally {
            setConfirmDialog({ open: false, ids: [], message: '' });
            if (selectionMode) {
                 setSelected(prev => prev.filter(id => !idsToDelete.includes(id)));
            }
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <StyledPaper>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid item>
                        <Typography variant="h6">Feedback Management</Typography>
                    </Grid>
                    <Grid item>
                        {selectionMode ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDeleteRequest(selected)}
                                    disabled={selected.length === 0}
                                >
                                    Delete ({selected.length})
                                </Button>
                                <Button size="small" onClick={handleExitSelectionMode}>Cancel</Button>
                            </Box>
                        ) : (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setSelectionMode(true)}
                                disabled={feedbackList.length === 0}
                            >
                                Select
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </StyledPaper>

            <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 120px)' }}>
                <Table size="small" stickyHeader>
                    <TableHead sx={{ backgroundColor: '#11224d' }}>
                        <TableRow>
                            {selectionMode && (
                                <TableCell padding="checkbox" sx={{ backgroundColor: '#11224d' }}>
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < feedbackList.length}
                                        checked={feedbackList.length > 0 && selected.length === feedbackList.length}
                                        onChange={handleSelectAll}
                                        sx={{ color: 'white' }}
                                    />
                                </TableCell>
                            )}
                            <TableCell sx={{ color: 'white', backgroundColor: '#11224d' }}>Company</TableCell>
                            <TableCell sx={{ color: 'white', backgroundColor: '#11224d' }}>Role</TableCell>
                            <TableCell sx={{ color: 'white', textAlign: 'center', backgroundColor: '#11224d' }}>Rating</TableCell>
                            <TableCell sx={{ color: 'white', backgroundColor: '#11224d' }}>Feedback</TableCell>
                            <TableCell sx={{ color: 'white', backgroundColor: '#11224d' }}>Date</TableCell>
                            <TableCell sx={{ color: 'white', backgroundColor: '#11224d', textAlign: 'right' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {feedbackList.map((item) => {
                            const isItemSelected = selected.indexOf(item.id) !== -1;
                            return (
                            <TableRow 
                                key={item.id} 
                                hover
                                role="checkbox"
                                aria-checked={isItemSelected}
                                selected={isItemSelected}
                                onClick={() => selectionMode && handleSelect(item.id)}
                                sx={{ cursor: selectionMode ? 'pointer' : 'default' }}
                            >
                                {selectionMode && (
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={isItemSelected} />
                                    </TableCell>
                                )}
                                <TableCell>{item.company_name}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={item.user_role}
                                        size="small"
                                        sx={{
                                            textTransform: 'capitalize',
                                            fontSize: '0.65rem',
                                            height: '20px',
                                            backgroundColor:
                                                item.user_role === 'shipper' ? '#c8e6c9' : 
                                                item.user_role === 'carrier' ? '#fff9c4' :
                                                item.user_role === 'broker'  ? '#bbdefb' : '#e0e0e0',
                                            color:
                                                item.user_role === 'shipper' ? '#1b5e20' : 
                                                item.user_role === 'carrier' ? '#f57f17' : 
                                                item.user_role === 'broker'  ? '#0d47a1' : '#000000', 
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Rating value={Number(item.rating)} readOnly size="small" />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 400, whiteSpace: 'pre-wrap' }}>
                                    {item.feedback_text}
                                </TableCell>
                                <TableCell>{dayjs(item.created_at).format('MM/DD/YYYY hh:mm A')}</TableCell>
                                <TableCell align="right">
                                    {!selectionMode && (
                                        <IconButton 
                                            size="small" 
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEnterSelectionMode(item.id);
                                            }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        )})}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>{confirmDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus>Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FeedbackManagement;
